import { create } from "zustand";
import { ApiError, admin, type AdminVocabRow, type VocabPayload, type VocabResetSummary } from "../lib/api";

type Status = "idle" | "loading" | "ready" | "error";

export type MutationResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | "english_duplicate_active"
        | "english_duplicate_deleted"
        | "validation"
        | "not_found"
        | "network"
        | "other";
      message: string;
    };

interface AdminVocabState {
  status: Status;
  rows: AdminVocabRow[];
  error: string | null;

  load: () => Promise<void>;
  create: (payload: VocabPayload) => Promise<MutationResult>;
  update: (english: string, payload: Omit<VocabPayload, "english">) => Promise<MutationResult>;
  softDelete: (english: string) => Promise<MutationResult>;
  restore: (english: string) => Promise<MutationResult>;
  /** Restore the entire vocab table to the immutable repo baseline.
   *  Returns a summary the UI can surface as a toast. */
  resetToBaseline: () => Promise<
    { ok: true; summary: VocabResetSummary } | { ok: false; message: string }
  >;
}

const VALIDATION_MESSAGES: Record<string, string> = {
  english_empty: "Englischer Begriff darf nicht leer sein.",
  english_too_long: "Englischer Begriff ist zu lang (max 100 Zeichen).",
  german_empty: "Deutscher Begriff darf nicht leer sein.",
  german_too_long: "Deutscher Begriff ist zu lang (max 200 Zeichen).",
  abbreviation_too_long: "Abkürzung ist zu lang (max 16 Zeichen).",
  examples_too_few: "Mindestens 2 Beispiele eintragen.",
  examples_too_many: "Maximal 5 Beispiele.",
  example_too_long: "Ein Beispielsatz ist zu lang (max 200 Zeichen).",
  english_duplicate_active:
    "Dieser englische Begriff ist bereits in der Liste — bitte bearbeiten statt neu anlegen.",
  english_duplicate_deleted:
    "Dieser englische Begriff existiert als gelöschter Eintrag. Wiederherstellen statt neu anlegen.",
};

function mapApiError(err: unknown): MutationResult {
  if (!(err instanceof ApiError)) {
    return { ok: false, reason: "other", message: String(err) };
  }
  if (err.kind === "network") {
    return { ok: false, reason: "network", message: "Keine Verbindung." };
  }
  if (err.status === 409 && err.message === "english_duplicate_active") {
    return {
      ok: false,
      reason: "english_duplicate_active",
      message: VALIDATION_MESSAGES.english_duplicate_active!,
    };
  }
  if (err.status === 409 && err.message === "english_duplicate_deleted") {
    return {
      ok: false,
      reason: "english_duplicate_deleted",
      message: VALIDATION_MESSAGES.english_duplicate_deleted!,
    };
  }
  if (err.status === 404) {
    return { ok: false, reason: "not_found", message: "Eintrag nicht gefunden." };
  }
  if (err.status === 400 && VALIDATION_MESSAGES[err.message]) {
    return { ok: false, reason: "validation", message: VALIDATION_MESSAGES[err.message]! };
  }
  return { ok: false, reason: "other", message: err.message };
}

export const useAdminVocabStore = create<AdminVocabState>((set, get) => ({
  status: "idle",
  rows: [],
  error: null,

  async load() {
    set({ status: "loading", error: null });
    try {
      const { vocab } = await admin.vocab.list();
      set({ status: "ready", rows: vocab, error: null });
    } catch (err) {
      const mapped = mapApiError(err);
      const message = mapped.ok ? "Unbekannter Fehler" : mapped.message;
      set({ status: "error", error: message });
    }
  },

  async create(payload) {
    try {
      const { vocab } = await admin.vocab.create(payload);
      set({ rows: [...get().rows, vocab].sort((a, b) => a.english.localeCompare(b.english)) });
      return { ok: true };
    } catch (err) {
      return mapApiError(err);
    }
  },

  async update(english, payload) {
    try {
      const { vocab } = await admin.vocab.update(english, payload);
      set({
        rows: get().rows.map((r) => (r.english === english ? vocab : r)),
      });
      return { ok: true };
    } catch (err) {
      return mapApiError(err);
    }
  },

  async softDelete(english) {
    try {
      await admin.vocab.softDelete(english);
      // Update local row to soft-deleted state without a full refetch.
      const now = new Date().toISOString();
      set({
        rows: get().rows.map((r) =>
          r.english === english ? { ...r, deleted_at: now, updated_at: now } : r,
        ),
      });
      return { ok: true };
    } catch (err) {
      return mapApiError(err);
    }
  },

  async restore(english) {
    try {
      const { vocab } = await admin.vocab.restore(english);
      set({
        rows: get().rows.map((r) => (r.english === english ? vocab : r)),
      });
      return { ok: true };
    } catch (err) {
      return mapApiError(err);
    }
  },

  async resetToBaseline() {
    try {
      const { summary } = await admin.vocab.reset();
      // Reload the full list so the UI reflects post-reset state (all rows
      // active, orphan rows gone, fresh timestamps).
      await get().load();
      return { ok: true, summary };
    } catch (err) {
      const mapped = mapApiError(err);
      return { ok: false, message: mapped.ok ? "Unbekannter Fehler" : mapped.message };
    }
  },
}));
